/**
 * pricingRuleWizardModel — state shape, payload builders and i18n labels for the
 * consolidated Pricing Rule + Coupon wizard at /inventory/pricing-rule/new.
 *
 * One wizard produces two possible outputs:
 *   - coupon OFF → a plain automatic Pricing Rule (pricingRules.create)
 *   - coupon ON  → a Pricing Rule + Coupon Code pair via the self-serve
 *                  admin_coupon.create_promotion endpoint (handles every field
 *                  below: scope, free item, free shipping, free embroidery,
 *                  customer group, storefront scope, usage cap).
 *
 * See docs plan 1936 for the capability→field map behind each option.
 */

export type RewardKind = "discount" | "product";
export type DiscountMode = "percentage" | "amount" | "rate";
/** null = no shipping perk; free = free shipping; amount/percentage = partial */
export type ShippingMode = "free" | "amount" | "percentage" | null;

/** What a coupon-gated customization requirement does when the coupon applies. */
export type ConditionalCustomizationAction = "require" | "free" | "require_free";

/**
 * One coupon-gated customization requirement (Group / Bulk Deal). Maps to a
 * `{template, field, action}` entry in the backend `conditional_customizations`
 * list on the Pricing Rule (see spec docs/architecture/group-order-conditional-deals-spec.md).
 */
export interface ConditionalCustomization {
  /** Customization Template name. */
  template: string;
  /** Field / slot within that template (e.g. the embroidery/logo slot). */
  field: string;
  /** require = force it; free = zero its charge; require_free = both. */
  action: ConditionalCustomizationAction;
}

export interface WizardState {
  // reward
  rewardKind: RewardKind;
  discountMode: DiscountMode | null;
  discountValue: number;
  sameItem: boolean;
  freeItem: string;
  freeQty: number;
  freeItemRate: number;
  // scope
  applyOn: string; // "" until chosen; then Transaction | Item Code | Item Group | Brand
  scopeValue: string;
  // perks
  freeShipping: ShippingMode;
  shippingValue: number;
  freeEmbroidery: boolean;
  // conditions
  minQty: string;
  minAmt: string;
  validFrom: string;
  validUpto: string;
  company: string;
  // storefront (non-coupon automatic rule visibility)
  storefrontEnabled: boolean;
  storefrontShowOnDetail: boolean;
  // coupon
  isCoupon: boolean;
  couponCode: string;
  couponPublic: boolean; // public = collectable by anyone; else exclusive/targeted
  maxUse: string; // per-code redemption cap
  maxCollections: string;
  customerGroup: string;
  /**
   * Per-store scope — comma-separated storefront_keys, blank = all storefronts.
   * Shared by BOTH paths: emitted as `storefront_scope` (coupon) and
   * `custom_storefront_scope` (automatic pricing rule).
   */
  storefrontScope: string;
  title: string;
  // Group / Bulk Deal — coupon-gated customization requirements. Empty ⇒ this is
  // a plain coupon (backward-safe); non-empty ⇒ emitted as `conditional_customizations`.
  conditionalCustomizations: ConditionalCustomization[];
}

export const INITIAL: WizardState = {
  rewardKind: "discount",
  discountMode: null,
  discountValue: 0,
  sameItem: false,
  freeItem: "",
  freeQty: 1,
  freeItemRate: 0,
  applyOn: "Transaction",
  scopeValue: "",
  freeShipping: null,
  shippingValue: 0,
  freeEmbroidery: false,
  minQty: "",
  minAmt: "",
  validFrom: "",
  validUpto: "",
  company: "",
  storefrontEnabled: true,
  storefrontShowOnDetail: true,
  isCoupon: false,
  couponCode: "",
  couponPublic: true,
  maxUse: "",
  maxCollections: "",
  customerGroup: "",
  storefrontScope: "",
  title: "",
  conditionalCustomizations: [],
};

// ── Helpers ─────────────────────────────────────────────────

/** "2026-08-10T14:30" (datetime-local) → "2026-08-10 14:30:00" (Frappe Datetime). */
export function toFrappeDatetime(v: string): string {
  if (!v) return "";
  const [d, t] = v.split("T");
  if (!t) return `${d} 00:00:00`;
  return `${d} ${t.length === 5 ? `${t}:00` : t}`;
}

/** Date portion of a datetime-local value → "2026-08-10". */
export function datePart(v: string): string {
  return v ? v.split("T")[0] : "";
}


/** Does the current config grant at least one benefit? (mirrors backend guard) */
export function hasBenefit(s: WizardState): boolean {
  const cartDiscount = s.rewardKind === "discount" && !!s.discountMode && s.discountValue > 0;
  const freeItem = s.rewardKind === "product" && (s.sameItem || !!s.freeItem) && s.freeQty > 0;
  const shipping = !!s.freeShipping;
  const embroidery = s.freeEmbroidery;
  return cartDiscount || freeItem || shipping || embroidery;
}

/** Generate an uppercase coupon code from the promotion title + a random suffix. */
export function generateCode(title: string): string {
  const base = (title || "PROMO")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "")
    .slice(0, 8);
  // Random suffix so "Randomize" produces a genuinely new code on every click.
  // This only runs inside click/change handlers — never during SSR render — so
  // Math.random is safe here (a deterministic suffix made the button a no-op).
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // drop ambiguous 0/O/1/I
  let suffix = "";
  for (let i = 0; i < 4; i++) suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `${base || "PROMO"}${suffix}`;
}

const SHIPPING_TYPE_MAP: Record<Exclude<ShippingMode, null>, string> = {
  free: "Free Shipping",
  amount: "Fixed Amount",
  percentage: "Percentage",
};

/**
 * Build the payload for a plain automatic Pricing Rule (coupon toggle OFF).
 * Perk custom fields are still written so an automatic rule can carry free
 * shipping / embroidery behavior.
 */
export function buildPricingRulePayload(s: WizardState): Record<string, unknown> {
  const data: Record<string, unknown> = {
    title: s.title,
    selling: 1,
    buying: 0,
    coupon_code_based: 0,
    apply_on: s.applyOn || "Transaction",
    min_qty: s.minQty ? parseFloat(s.minQty) : 0,
    min_amt: s.minAmt ? parseFloat(s.minAmt) : 0,
    // Date part feeds ERPNext's native (date-level) pricing engine; the
    // Datetime campaign fields carry the hour-precise flash-sale window.
    valid_from: datePart(s.validFrom) || null,
    valid_upto: datePart(s.validUpto) || null,
    custom_campaign_start: s.validFrom ? toFrappeDatetime(s.validFrom) : null,
    custom_campaign_end: s.validUpto ? toFrappeDatetime(s.validUpto) : null,
    company: s.company || null,
    storefront_enabled: s.storefrontEnabled ? 1 : 0,
    storefront_show_on_detail: s.storefrontShowOnDetail ? 1 : 0,
    // Per-store scope: comma-separated storefront_keys, blank = all storefronts.
    // Shares the same wizard field as the coupon path (concept is identical).
    custom_storefront_scope: s.storefrontScope || "",
  };

  if (s.rewardKind === "product") {
    data.price_or_product_discount = "Product";
    data.same_item = s.sameItem ? 1 : 0;
    data.free_item = s.sameItem ? "" : s.freeItem;
    data.free_qty = s.freeQty;
    data.free_item_rate = s.freeItemRate;
  } else {
    data.price_or_product_discount = "Price";
    if (s.discountMode === "percentage") {
      data.rate_or_discount = "Discount Percentage";
      data.discount_percentage = s.discountValue;
    } else if (s.discountMode === "amount") {
      data.rate_or_discount = "Discount Amount";
      data.discount_amount = s.discountValue;
    } else if (s.discountMode === "rate") {
      data.rate_or_discount = "Rate";
      data.rate = s.discountValue;
    }
  }

  // Perks
  if (s.freeShipping) {
    data.custom_shipping_discount_type = SHIPPING_TYPE_MAP[s.freeShipping];
    data.custom_shipping_discount_value = s.freeShipping === "free" ? 0 : s.shippingValue;
  }
  if (s.freeEmbroidery) data.custom_is_embroidery_coupon = 1;

  // Scope child table
  if (s.applyOn === "Item Code" && s.scopeValue) data.items = [{ item_code: s.scopeValue }];
  else if (s.applyOn === "Item Group" && s.scopeValue) data.item_groups = [{ item_group: s.scopeValue }];
  else if (s.applyOn === "Brand" && s.scopeValue) data.brands = [{ brand: s.scopeValue }];

  return data;
}

/**
 * Build the params for admin_coupon.create_promotion (coupon toggle ON).
 * Omits empty values so the backend keeps its own defaults.
 */
export function buildCouponParams(s: WizardState): Record<string, unknown> {
  const p: Record<string, unknown> = {
    promotion_title: s.title,
    coupon_code: s.couponCode,
    apply_on: s.applyOn || "Transaction",
    // Hour-precise window; backend derives the coarse valid_from/valid_upto date.
    valid_from: datePart(s.validFrom),
    valid_upto: datePart(s.validUpto),
    campaign_start: toFrappeDatetime(s.validFrom),
    campaign_end: toFrappeDatetime(s.validUpto),
    is_public: s.couponPublic ? 1 : 0,
  };

  if (s.rewardKind === "product") {
    p.reward_kind = "Product";
    p.free_item = s.sameItem ? "" : s.freeItem;
    p.same_item = s.sameItem ? 1 : 0;
    p.free_qty = s.freeQty;
    if (s.freeItemRate) p.free_item_rate = s.freeItemRate;
  } else {
    p.reward_kind = "Price";
    // Coupons support percentage/amount cart discounts (not absolute "rate")
    if (s.discountMode === "percentage" && s.discountValue > 0) {
      p.discount_type = "Percentage";
      p.discount_value = s.discountValue;
    } else if (s.discountMode === "amount" && s.discountValue > 0) {
      p.discount_type = "Amount";
      p.discount_value = s.discountValue;
    }
  }

  if (s.applyOn && s.applyOn !== "Transaction" && s.scopeValue) p.scope_value = s.scopeValue;

  // Perks
  if (s.freeShipping) {
    p.shipping_discount_type = SHIPPING_TYPE_MAP[s.freeShipping];
    if (s.freeShipping !== "free") p.shipping_discount_value = s.shippingValue;
  }
  if (s.freeEmbroidery) p.is_embroidery_coupon = 1;

  // Conditions
  if (s.minAmt) p.minimum_order = parseFloat(s.minAmt);
  if (s.minQty) p.minimum_cart_qty = parseInt(s.minQty, 10);

  // Coupon meta
  if (s.maxUse) p.maximum_use = parseInt(s.maxUse, 10);
  if (s.maxCollections) p.max_collections = parseInt(s.maxCollections, 10);
  if (s.customerGroup) p.custom_target_customer_group = s.customerGroup;
  if (s.storefrontScope) p.storefront_scope = s.storefrontScope;

  // Group / Bulk Deal — coupon-gated customization requirements.
  // The threshold (min_qty/min_amt), free shipping, free embroidery and campaign
  // window are ALREADY mapped above onto the existing coupon params; a group-order
  // deal only ADDS the conditional customization list. Sent as a JSON array — the
  // ERP client JSON-encodes the whole body, so the backend receives a proper list
  // of {template, field, action} (mirrors how items/item_groups are passed).
  const conditionalCustomizations = (s.conditionalCustomizations || []).filter(
    (c) => c.template && c.field && c.action,
  );
  if (conditionalCustomizations.length) {
    p.conditional_customizations = conditionalCustomizations;
  }

  return p;
}

// ── i18n labels ─────────────────────────────────────────────

export const labels: Record<string, { en: string; th: string }> = {
  page_title: { en: "New Promotion", th: "สร้างโปรโมชั่นใหม่" },
  page_desc: { en: "Build a discount, free item or coupon for your storefront", th: "สร้างส่วนลด สินค้าแถม หรือคูปองสำหรับหน้าร้าน" },
  reward_header: { en: "Reward", th: "สิทธิประโยชน์" },
  discount_header: { en: "Discount", th: "ส่วนลด" },
  free_item_header: { en: "Free Item", th: "สินค้าแถม" },
  scope_header: { en: "Applies To", th: "ใช้กับ" },
  perks_header: { en: "Extra Perks", th: "สิทธิพิเศษเพิ่มเติม" },
  conditions_header: { en: "Conditions", th: "เงื่อนไข" },
  coupon_header: { en: "Coupon Code", th: "โค้ดคูปอง" },
  name_header: { en: "Promotion Name", th: "ชื่อโปรโมชั่น" },
  create_btn: { en: "Create Promotion", th: "สร้างโปรโมชั่น" },
  create_coupon_btn: { en: "Create Coupon", th: "สร้างคูปอง" },
  creating_btn: { en: "Creating...", th: "กำลังสร้าง..." },
  reward_discount: { en: "Discount", th: "ส่วนลด" },
  reward_discount_desc: { en: "% or ฿ off the price", th: "ลดราคาเป็น % หรือ ฿" },
  reward_product: { en: "Free item", th: "แถมสินค้า" },
  reward_product_desc: { en: "Buy X get Y free", th: "ซื้อ X แถม Y" },
  same_item: { en: "Same as purchased", th: "สินค้าเดียวกับที่ซื้อ" },
  same_item_desc: { en: "Buy 1 get 1 of the same", th: "ซื้อ 1 แถม 1 ตัวเดียวกัน" },
  free_item: { en: "Free item", th: "สินค้าแถม" },
  free_qty: { en: "Free quantity", th: "จำนวนแถม" },
  free_shipping: { en: "Free / discounted shipping", th: "ส่งฟรีหรือลดค่าส่ง" },
  free_shipping_desc: { en: "Waive or reduce the delivery fee", th: "ยกเว้นหรือลดค่าจัดส่ง" },
  free_embroidery: { en: "Free personalization", th: "ปรับแต่งฟรี" },
  free_embroidery_desc: { en: "Waive customization / add-on service fees (embroidery, engraving, etc.)", th: "ยกเว้นค่าบริการปรับแต่งสินค้า (เช่น ปักชื่อ สลักชื่อ)" },
  min_qty: { en: "Minimum quantity", th: "จำนวนขั้นต่ำ" },
  min_qty_hint: { en: "How many items must be in the cart to qualify. Leave empty for no minimum.", th: "ต้องมีสินค้ากี่ชิ้นในตะกร้าจึงจะใช้สิทธิ์ได้ เว้นว่างหากไม่กำหนด" },
  min_amt: { en: "Minimum order (฿)", th: "ยอดสั่งซื้อขั้นต่ำ (฿)" },
  min_amt_hint: { en: "Cart total must reach this amount before the promotion applies.", th: "ยอดรวมในตะกร้าต้องถึงจำนวนนี้ก่อนจึงจะใช้โปรโมชั่นได้" },
  valid_dates: { en: "Campaign window (date & time)", th: "ช่วงเวลาแคมเปญ (วันและเวลา)" },
  valid_dates_hint: { en: "When the promotion starts and ends. Set a time for hour-precise countdowns.", th: "เวลาเริ่มและสิ้นสุดโปรโมชั่น ระบุเวลาเพื่อทำการนับถอยหลังแบบละเอียดเป็นชั่วโมง" },
  company: { en: "Company", th: "บริษัท" },
  company_hint: { en: "The store this promotion belongs to.", th: "ร้าน/บริษัทที่โปรโมชั่นนี้สังกัด" },
  checkout_toggle: { en: "Apply at checkout", th: "ใช้ตอนชำระเงิน" },
  detail_toggle: { en: "Show on product page", th: "แสดงในหน้าสินค้า" },
  name_placeholder: { en: "Give your promotion a name...", th: "ตั้งชื่อโปรโมชั่น..." },
  make_coupon: { en: "Make it a coupon code", th: "ทำเป็นโค้ดคูปอง" },
  make_coupon_desc: { en: "Customers enter a code at checkout to redeem", th: "ลูกค้าใส่โค้ดตอนชำระเงินเพื่อรับสิทธิ์" },
  code_label: { en: "Coupon code", th: "โค้ดคูปอง" },
  code_hint: { en: "The code customers type at checkout to redeem this promotion.", th: "โค้ดที่ลูกค้าพิมพ์ตอนชำระเงินเพื่อรับสิทธิ์โปรโมชั่นนี้" },
  code_generate: { en: "Generate", th: "สุ่มโค้ด" },
  public_label: { en: "Public — anyone can collect", th: "สาธารณะ — ใครก็เก็บได้" },
  public_desc: { en: "Off = exclusive / targeted only", th: "ปิด = เฉพาะกลุ่มเป้าหมาย" },
  max_use: { en: "Total redemption limit", th: "จำนวนครั้งที่ใช้ได้สูงสุด" },
  max_use_hint: { en: "Total times this code can be used across all customers.", th: "จำนวนครั้งรวมที่โค้ดนี้ถูกใช้ได้จากลูกค้าทุกคน" },
  max_collections: { en: "Collection limit", th: "จำนวนที่เก็บได้สูงสุด" },
  max_collections_hint: { en: "How many customers can collect this coupon to keep.", th: "จำนวนลูกค้าที่เก็บคูปองนี้ไว้ได้" },
  customer_group: { en: "Target customer group", th: "กลุ่มลูกค้าเป้าหมาย" },
  customer_group_hint: { en: "Restrict to one customer group. Leave empty for all customers.", th: "จำกัดเฉพาะกลุ่มลูกค้าเดียว เว้นว่างสำหรับลูกค้าทุกคน" },
  storefront_scope: { en: "Storefront scope", th: "ขอบเขตหน้าร้าน" },
  storefront_scope_hint: { en: "Limit to specific storefronts. Leave empty to allow all.", th: "จำกัดเฉพาะบางหน้าร้าน เว้นว่างเพื่อใช้ได้ทุกหน้าร้าน" },
  storefront_scope_ph: { en: "All storefronts", th: "ทุกหน้าร้าน" },
  shipping_free: { en: "Free", th: "ฟรี" },
  shipping_amount: { en: "฿ off", th: "ลด ฿" },
  shipping_percentage: { en: "% off", th: "ลด %" },
  storefront_header: { en: "Storefront Visibility", th: "การแสดงผลหน้าร้าน" },
  conditional_header: { en: "Conditional Customization", th: "การปรับแต่งแบบมีเงื่อนไข" },
  conditional_desc: { en: "When this coupon applies, force or free a product customization (e.g. require + free logo embroidery on group orders).", th: "เมื่อใช้คูปองนี้ บังคับหรือยกเว้นค่าปรับแต่งสินค้า (เช่น บังคับ + ปักโลโก้ฟรีสำหรับออร์เดอร์กลุ่ม)" },
  conditional_add: { en: "Add customization rule", th: "เพิ่มเงื่อนไขการปรับแต่ง" },
  conditional_template: { en: "Template", th: "เทมเพลต" },
  conditional_field: { en: "Field / slot", th: "ฟิลด์ / ช่อง" },
  conditional_field_ph: { en: "e.g. logo_embroidery", th: "เช่น logo_embroidery" },
  conditional_action: { en: "Action", th: "การกระทำ" },
  conditional_action_require: { en: "Require", th: "บังคับ" },
  conditional_action_free: { en: "Make free", th: "ยกเว้นค่าบริการ" },
  conditional_action_require_free: { en: "Require + free", th: "บังคับ + ฟรี" },
  conditional_remove: { en: "Remove", th: "ลบ" },
};
